import React, { useState } from "react";
import { Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";

export default function PricingCards({ plans, compact = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null);

  const subscribe = async (plan) => {
    if (!user) {
      navigate("/register?next=/pricing");
      return;
    }
    if (user.account_type === "sponsor") {
      toast.info("Sponsor accounts already have unlimited messaging, free forever.");
      return;
    }
    setBusy(plan.lookup_key);
    try {
      const { data } = await api.post("/billing/checkout", {
        lookup_key: plan.lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiError(e, "Could not start checkout."));
      setBusy(null);
    }
  };

  const currentPlan = user?.subscription_plan;

  return (
    <div className={`grid gap-5 ${compact ? "sm:grid-cols-3" : "md:grid-cols-3"}`}>
      {plans.map((plan) => {
        const recommended = plan.badge === "Recommended";
        const isCurrent = currentPlan === plan.lookup_key && user?.subscription_status === "active";
        return (
          <div
            key={plan.lookup_key}
            data-testid={`plan-card-${plan.lookup_key}`}
            className={`relative rounded-2xl bg-white p-6 flex flex-col ${
              recommended
                ? "border-2 border-[#1F6F78] shadow-[0_14px_40px_rgba(31,42,46,0.14)]"
                : "border border-[#E3DED4] shadow-[0_10px_30px_rgba(31,42,46,0.07)]"
            }`}
          >
            {plan.badge && (
              <span
                className={`absolute -top-3 left-6 px-3 py-1 rounded-full text-xs font-bold ${
                  recommended ? "bg-[#1F6F78] text-white" : "bg-[#F6E2D6] text-[#8A4B22]"
                }`}
              >
                {plan.badge}
              </span>
            )}
            <h3 className="font-display text-xl font-bold text-[#1F2A2E]">{plan.label}</h3>
            <p className="text-sm text-[#4B5A60] mt-0.5">{plan.tagline}</p>
            <div className="mt-4 flex items-baseline gap-1">
              <span
                data-testid={`plan-price-${plan.lookup_key}`}
                className="font-display text-4xl font-bold text-[#1F2A2E]"
              >
                {plan.price_display}
              </span>
              <span className="text-sm text-[#4B5A60]">{plan.period_display}</span>
            </div>
            <ul className="mt-5 space-y-2.5 flex-1">
              {plan.benefits.map((b) => (
                <li key={b} className="flex items-start gap-2 text-sm text-[#1F2A2E]">
                  <Check className="w-4 h-4 mt-0.5 text-[#1F7A4D] shrink-0" />
                  {b}
                </li>
              ))}
            </ul>
            <Button
              onClick={() => subscribe(plan)}
              disabled={busy === plan.lookup_key || isCurrent}
              data-testid={`subscribe-button-${plan.lookup_key}`}
              className={`mt-6 w-full ${
                recommended
                  ? "bg-[#1F6F78] hover:bg-[#185B62] text-white"
                  : "bg-white border border-[#1F6F78] text-[#1F6F78] hover:bg-[#D7EEF0]"
              }`}
            >
              {busy === plan.lookup_key ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Opening checkout
                </>
              ) : isCurrent ? (
                "Your current plan"
              ) : (
                `Choose ${plan.label}`
              )}
            </Button>
          </div>
        );
      })}
    </div>
  );
}
